import { BookOpenIcon, ChartBarIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { ActionIcon, Box, Collapse, Loader, Paper, Text, Tooltip, UnstyledButton, useMantineColorScheme } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { getScoreColor } from '../utils/scoreUtils';
import { CircularProgress } from './CircularProgress';

interface ProfileCompletionSummaryProps {
    personId: number | null | undefined;
}

export const ProfileCompletionSummary = ({ personId }: ProfileCompletionSummaryProps) => {
    const [open, setOpen] = useState(false);
    const { data, isLoading } = useProfileCompletion(personId);
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { t } = useTranslation();

    if (isLoading) {
        return <Loader size="xs" />;
    }

    if (!data) {
        return null;
    }

    const pct = Math.round(data.finalScore * 100);
    const color = getScoreColor(data.finalScore);

    const formattedDate = new Date(data.calculatedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <Box>
            {/* Pill trigger + FAQ link */}
            <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <UnstyledButton onClick={() => setOpen((prev) => !prev)}>
                    <Box
                        px={12}
                        py={5}
                        style={{
                            background: color.brandColor,
                            borderRadius: 99,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <ChartBarIcon style={{ width: 14, height: 14, color: 'white', flexShrink: 0 }} />
                        <Text size="xs" fw={700} c="white">
                            {t('PROFILE_COMPLETION_TRIGGER_LABEL', { score: String(pct) })}
                        </Text>
                        <ChevronDownIcon style={{
                            width: 12,
                            height: 12,
                            color: 'white',
                            flexShrink: 0,
                            transition: 'transform 0.2s ease',
                            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                        }} />
                    </Box>
                </UnstyledButton>

                <Tooltip label="Read more in FAQ" position="right" withArrow>
                    <ActionIcon
                        component="a"
                        href="/faq/profile-completion"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="subtle"
                        color="brand"
                        size="sm"
                        aria-label="Read more in FAQ"
                    >
                        <BookOpenIcon style={{ width: 16, height: 16 }} />
                    </ActionIcon>
                </Tooltip>
            </Box>

            {/* Expanded panel */}
            <Collapse in={open}>
                <Paper
                    mt={6}
                    radius="md"
                    withBorder={false}
                    style={{ overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}
                >
                    {/* Split layout: gradient ring left · plain sections right */}
                    <Box style={{ display: 'flex', alignItems: 'stretch' }}>

                        {/* Gradient ring column */}
                        <Box
                            px={14}
                            py={12}
                            style={{
                                background: 'linear-gradient(135deg, var(--mantine-color-brand-7) 0%, var(--mantine-color-brand-9) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <CircularProgress score={data.finalScore} size={76} strokeWidth={7} onDark />
                        </Box>

                        {/* Sections column */}
                        <Box
                            px={12}
                            py={10}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                background: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                            }}
                        >
                            {data.sections.map((section) => {
                                const sc = getScoreColor(section.normalizedScore);
                                const sPct = Math.round(section.normalizedScore * 100);
                                return (
                                    <Tooltip
                                        key={section.sectionId}
                                        label={section.sectionRulesRationale}
                                        multiline
                                        w={260}
                                        withArrow
                                        disabled={!section.sectionRulesRationale}
                                    >
                                        <Box
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                marginBottom: 4,
                                                cursor: section.sectionRulesRationale ? 'help' : 'default',
                                            }}
                                        >
                                            <Text
                                                size="xs"
                                                style={{ flex: 1, minWidth: 0 }}
                                                truncate="end"
                                            >
                                                {section.sectionName}
                                            </Text>
                                            <Box style={{ width: 44, flexShrink: 0 }}>
                                                <Box
                                                    style={{
                                                        height: 4,
                                                        background: isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-2)',
                                                        borderRadius: 99,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Box
                                                        style={{
                                                            width: `${sPct}%`,
                                                            height: '100%',
                                                            background: sc.brandColor,
                                                            borderRadius: 99,
                                                            transition: 'width 0.6s ease',
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            <Text
                                                size="xs"
                                                fw={700}
                                                style={{ color: sc.brandColor, minWidth: 28, textAlign: 'right', flexShrink: 0 }}
                                            >
                                                {sPct}%
                                            </Text>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* Footer */}
                    <Box
                        px={10}
                        py={5}
                        style={{
                            background: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)',
                            borderTop: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                        }}
                    >
                        <Text size="xs" c="dimmed">
                            {t('PROFILE_COMPLETION_UPDATED_AT', { date: formattedDate })}
                        </Text>
                    </Box>
                </Paper>
            </Collapse>
        </Box>
    );
};


